import { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { api } from './api.js';
import Spinner from './components/Spinner.jsx';

// ─── Kontext ─────────────────────────────────────────────────────────────────
const UserCtx = createContext(null);
export const useUser = () => useContext(UserCtx);

// ─── Imports ─────────────────────────────────────────────────────────────────
import Dashboard       from './ausbilder/Dashboard.jsx';
import StudentDetail   from './ausbilder/StudentDetail.jsx';
import VorDemDienst    from './ausbilder/VorDemDienst.jsx';
import DiveLog         from './ausbilder/DiveLog.jsx';
import QuizStats            from './ausbilder/QuizStats.jsx';
import Ausbildungsnachweis  from './ausbilder/Ausbildungsnachweis.jsx';
import UserManagement  from './admin/UserManagement.jsx';
import Reports         from './admin/Reports.jsx';
import Lernbereich     from './schueler/Lernbereich.jsx';
import Quiz            from './schueler/Quiz.jsx';
import Karteikarten    from './schueler/Karteikarten.jsx';
import LernreiheView   from './schueler/LernreiheView.jsx';

// ─── Build-Version (wird im CI-Build als VITE_BUILD_VERSION gesetzt) ─────────
const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION ?? 'dev';

// ─── Rollen-Hilfsfunktionen ───────────────────────────────────────────────────
const isAdmin      = (user) => user?.role === 'admin';
const isAusbilder  = (user) => user?.role === 'admin' || user?.role === 'ausbilder';

// ─── Header ──────────────────────────────────────────────────────────────────
function Header({ user }) {
  return (
    <header className="relative text-white shadow-lg overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1929 0%, #0d3a5c 60%, #0a5a7c 100%)' }}>
      <div className="max-w-4xl mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between relative z-10 gap-2">
        <Link to="/" className="flex items-center gap-2 font-bold tracking-wide hover:text-ocean-200 transition-colors shrink-0">
          <span className="text-xl sm:text-2xl" aria-hidden="true">🤿</span>
          <span className="text-sm sm:text-base">
            FW-Taucher
            <span className="text-ocean-300 font-normal hidden sm:inline"> Ausbildung</span>
          </span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm min-w-0">
          {isAusbilder(user) && (
            <Link to="/ausbilder" className="hover:text-ocean-300 transition-colors font-medium whitespace-nowrap">Schüler</Link>
          )}
          <Link to="/lernen" className="hover:text-ocean-300 transition-colors font-medium">Lernen</Link>
          {isAdmin(user) && (
            <>
              <Link to="/admin/users" className="hover:text-ocean-300 transition-colors flex items-center gap-1 whitespace-nowrap">
                <span className="badge bg-coral-500 text-white text-xs">Admin</span>
                <span className="hidden sm:inline">Benutzer</span>
              </Link>
              <Link to="/admin/reports" aria-label="Meldungen"
                className="hover:text-ocean-300 transition-colors whitespace-nowrap">
                <span aria-hidden="true">⚑</span>
              </Link>
            </>
          )}
          <span className="text-ocean-300 text-xs border-l border-ocean-700 pl-2 sm:pl-4 truncate max-w-[80px] sm:max-w-none">
            {user?.display_name ?? user?.email?.split('@')[0]}
          </span>
          <button
            onClick={() => { window.location.href = '/cdn-cgi/access/logout'; }}
            className="text-xs text-ocean-300 hover:text-white border border-ocean-700 hover:border-ocean-400 rounded px-2 py-0.5 transition-colors whitespace-nowrap shrink-0"
            title="Abmelden">
            Abmelden
          </button>
        </nav>
      </div>
      {/* Wellentrennlinie */}
      <svg viewBox="0 0 1200 20" className="w-full absolute bottom-0 left-0" preserveAspectRatio="none" style={{ height: '12px' }}>
        <path d="M0,10 C200,0 400,20 600,10 C800,0 1000,20 1200,10 L1200,20 L0,20 Z" fill="rgb(248 250 252)" />
      </svg>
    </header>
  );
}

// ─── Guards ──────────────────────────────────────────────────────────────────
function RequireAusbilder({ children }) {
  const user = useUser();
  if (!isAusbilder(user)) return <Navigate to="/" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const user = useUser();
  if (!isAdmin(user)) return <Navigate to="/" replace />;
  return children;
}

// ─── Root-Redirect ────────────────────────────────────────────────────────────
function Root() {
  const user = useUser();
  if (!user) return null;
  if (isAusbilder(user)) return <Navigate to="/ausbilder" replace />;
  return <Navigate to="/lernen" replace />;
}

// ─── App ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getMe()
      .then(setUser)
      .catch((e) => { setUser(null); setError(e.message); });
  }, []);

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-sm w-full text-center space-y-3">
          <p className="text-2xl">🚫</p>
          <p className="font-semibold text-gray-800">Kein Zugang</p>
          <p className="text-sm text-gray-500">{error ?? 'Nicht authentifiziert'}</p>
          {/* Volle Navigation (kein Router-Link): löst bei abgelaufener Session den Cloudflare-Access-Login aus */}
          <a href="/" className="btn-primary justify-center w-full">Neu anmelden</a>
        </div>
      </div>
    );
  }

  return (
    <UserCtx.Provider value={user}>
      <BrowserRouter>
        <div className="min-h-screen flex flex-col">
          <Header user={user} />
          <main className="flex-1 max-w-4xl w-full mx-auto px-3 sm:px-4 py-4 sm:py-6">
            <Routes>
              <Route path="/" element={<Root />} />

              {/* Ausbilder + Admin */}
              <Route path="/ausbilder" element={
                <RequireAusbilder><Dashboard /></RequireAusbilder>
              } />
              <Route path="/ausbilder/schueler/:id" element={
                <RequireAusbilder><StudentDetail /></RequireAusbilder>
              } />
              <Route path="/ausbilder/schueler/:id/vordienst" element={
                <RequireAusbilder><VorDemDienst /></RequireAusbilder>
              } />
              <Route path="/ausbilder/schueler/:id/tauchgaenge" element={
                <RequireAusbilder><DiveLog /></RequireAusbilder>
              } />
              <Route path="/ausbilder/schueler/:id/quiz-stats" element={
                <RequireAusbilder><QuizStats /></RequireAusbilder>
              } />
              <Route path="/ausbilder/schueler/:id/nachweis" element={
                <RequireAusbilder><Ausbildungsnachweis /></RequireAusbilder>
              } />

              {/* Admin only */}
              <Route path="/admin/users" element={
                <RequireAdmin><UserManagement /></RequireAdmin>
              } />
              <Route path="/admin/reports" element={
                <RequireAdmin><Reports /></RequireAdmin>
              } />

              {/* Alle */}
              <Route path="/lernen"                element={<Lernbereich />} />
              <Route path="/lernen/quiz"           element={<Quiz />} />
              <Route path="/lernen/karteikarten"   element={<Karteikarten />} />
              <Route path="/lernen/lernreihe/:id"  element={<LernreiheView />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <footer className="text-center text-xs text-gray-400 py-3 print:hidden">
            FW-Taucher Ausbildung · Version <span className="font-mono">{BUILD_VERSION}</span>
          </footer>
        </div>
      </BrowserRouter>
    </UserCtx.Provider>
  );
}
